import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Bell, ChevronDown, ChevronUp, Plus, Trash2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { AdminReminder } from "@shared/schema";

const addReminderSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
});
type AddReminderForm = z.infer<typeof addReminderSchema>;

const QUERY_KEY = ["/api/admin/reminders"];

export function AdminRemindersBanner() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  const form = useForm<AddReminderForm>({
    resolver: zodResolver(addReminderSchema),
    defaultValues: { title: "", description: "" },
  });

  const { data: reminders = [], isLoading } = useQuery<AdminReminder[]>({
    queryKey: QUERY_KEY,
    staleTime: 0,
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ id, resolve }: { id: string; resolve: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/reminders/${id}`, { resolve });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      toast({ title: "Failed to update reminder", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/reminders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: "Reminder deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete reminder", variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: AddReminderForm) => {
      const res = await apiRequest("POST", "/api/admin/reminders", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      form.reset();
      setShowAddForm(false);
      toast({ title: "Reminder added" });
    },
    onError: () => {
      toast({ title: "Failed to add reminder", variant: "destructive" });
    },
  });

  const open = reminders.filter(r => !r.isResolved);
  const resolved = reminders.filter(r => r.isResolved);

  if (isLoading) return null;
  if (reminders.length === 0) return null;
  if (open.length === 0 && !showAddForm) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className="border-b bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
        data-testid="admin-reminders-banner"
      >
        {/* Header row */}
        <div className="flex items-center justify-between px-4 py-2">
          <CollapsibleTrigger asChild>
            <button
              className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200"
              data-testid="button-toggle-reminders"
            >
              <Bell className="h-4 w-4" />
              <span>
                {open.length} action{open.length !== 1 ? "s" : ""} needed
              </span>
              {isOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
          </CollapsibleTrigger>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
              onClick={() => { setShowAddForm(v => !v); setIsOpen(true); }}
              data-testid="button-add-reminder"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Collapsible body */}
        <CollapsibleContent>
          <div className="px-4 pb-3 space-y-1.5">
            {open.map(reminder => (
              <ReminderRow
                key={reminder.id}
                reminder={reminder}
                onToggle={() => resolveMutation.mutate({ id: reminder.id, resolve: !reminder.isResolved })}
                onDelete={() => deleteMutation.mutate(reminder.id)}
              />
            ))}

            {resolved.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-amber-600 dark:text-amber-500 cursor-pointer select-none">
                  {resolved.length} resolved item{resolved.length !== 1 ? "s" : ""}
                </summary>
                <div className="mt-1.5 space-y-1.5 opacity-60">
                  {resolved.map(reminder => (
                    <ReminderRow
                      key={reminder.id}
                      reminder={reminder}
                      onToggle={() => resolveMutation.mutate({ id: reminder.id, resolve: false })}
                      onDelete={() => deleteMutation.mutate(reminder.id)}
                    />
                  ))}
                </div>
              </details>
            )}

            {showAddForm && (
              <div className="mt-2 rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-amber-950/30 p-3">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(data => createMutation.mutate(data))}
                    className="space-y-2"
                  >
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Reminder</FormLabel>
                          <FormControl>
                            <Input
                              className="h-7 text-sm"
                              placeholder="e.g. Update About page copy"
                              {...field}
                              data-testid="input-reminder-title"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Details (optional)</FormLabel>
                          <FormControl>
                            <Textarea
                              className="text-sm min-h-[60px] resize-none"
                              placeholder="Any extra context..."
                              {...field}
                              data-testid="input-reminder-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => { setShowAddForm(false); form.reset(); }}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={createMutation.isPending}
                        data-testid="button-save-reminder"
                      >
                        Save reminder
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function ReminderRow({
  reminder,
  onToggle,
  onDelete,
}: {
  reminder: AdminReminder;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="flex items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-amber-100/60 dark:hover:bg-amber-900/30 group"
      data-testid={`reminder-row-${reminder.id}`}
    >
      <Checkbox
        checked={reminder.isResolved}
        onCheckedChange={onToggle}
        className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
        data-testid={`checkbox-reminder-${reminder.id}`}
      />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium leading-tight ${reminder.isResolved ? "line-through text-muted-foreground" : "text-amber-900 dark:text-amber-100"}`}>
          {reminder.title}
        </p>
        {reminder.description && (
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-snug">
            {reminder.description}
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-amber-600 hover:text-destructive hover:bg-transparent"
        onClick={onDelete}
        data-testid={`button-delete-reminder-${reminder.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
